import React from 'react'
import clsx from 'clsx'
import type { Team, Step } from '../../types/session.types'
import { TeamProgressBar } from './TeamProgressBar'
import { TeamStatusBadge } from './TeamStatusBadge'
import { useIsTeamOnline } from '../../stores/selectors'
import styles from './TeamCard.module.css'

export interface TeamCardProps {
  team: Team
  steps: Step[]
  onClick: () => void
  className?: string
}

export function TeamCard({ team, steps, onClick, className }: TeamCardProps) {
  const isOnline = useIsTeamOnline(team.id)

  const stepsTotal = steps.length
  const stepsDone = steps.filter((s) => s.status === 'done' || s.status === 'skipped').length
  const stepsFailed = steps.filter((s) => s.status === 'failed').length
  const currentStep = steps.find((s) => s.status === 'in_progress')
  const progress = stepsTotal > 0 ? (stepsDone / stepsTotal) * 100 : 0

  const cardStatus = stepsFailed > 0
    ? 'failed'
    : !isOnline
    ? 'offline'
    : stepsDone === stepsTotal && stepsTotal > 0
    ? 'done'
    : 'default'

  return (
    <button
      type="button"
      className={clsx(styles.card, styles[`card_${cardStatus}`], className)}
      onClick={onClick}
      aria-label={`${team.name} — click to view details`}
      title={`${team.name}: ${stepsDone}/${stepsTotal} steps done`}
    >
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.icon}>{team.icon || '👥'}</span>
        <span className={styles.name}>{team.slug.toUpperCase()}</span>
        <TeamStatusBadge presence={isOnline ? 'online' : 'offline'} />
      </div>

      {/* Progress Bar */}
      <TeamProgressBar progress={progress} status={cardStatus} />

      {/* Step count */}
      <div className={styles.meta}>
        <span className={styles.stepCount}>
          {stepsDone}/{stepsTotal} steps
        </span>
        {stepsFailed > 0 && (
          <span className={styles.failedBadge}>{stepsFailed} FAILED</span>
        )}
      </div>

      {/* Current step name */}
      {currentStep && (
        <div className={styles.currentStep} title={currentStep.name}>
          ▶ {currentStep.name}
        </div>
      )}

      {/* All done indicator */}
      {stepsDone === stepsTotal && stepsTotal > 0 && (
        <div className={styles.doneBadge}>✅ DONE</div>
      )}
    </button>
  )
}
